import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { XIcon } from './icons';

/**
 * A short confirmation after something the visitor did ("Saved on this device…", "Link copied").
 * One at a time; a new one replaces the last. Never used to prompt, sell or hurry anyone.
 *
 * Fixed, so it never moves the page. On phones it sits above the event page's buy bar
 * (src/components/StickyBuyBar.tsx, ~62 px plus the safe area); from lg up, bottom left, clear of
 * the price board's prices and "Get tickets" column.
 * The live region is in the page from mount on, so screen readers announce each message; the
 * timer pauses while the pointer or focus is on it, and Esc closes it from inside.
 *
 * Mounted once, by the site header (src/components/Navbar.tsx), into document.body.
 */
export interface ToastSpec {
  /** already translated */
  message: string;
  link?: { to: string; label: string };
  action?: { label: string; run: () => void };
  /** how long it stays, default 5 s */
  ms?: number;
}

type Shown = ToastSpec & { key: number };
let current: Shown | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export const showToast = (spec: ToastSpec) => {
  current = { ...spec, key: ++seq };
  emit();
};
export const hideToast = () => {
  current = null;
  emit();
};

export const ToastHost = () => {
  const t = useT();
  const toast = useSyncExternalStore(subscribe, () => current, () => null);
  const [mounted, setMounted] = useState(false);
  const [held, setHeld] = useState(false);
  const [entered, setEntered] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setMounted(true), []);

  // Slide in on the next frame (transform + opacity only).
  useEffect(() => {
    setEntered(false);
    setHeld(false);
    if (!toast) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [toast]);

  useEffect(() => {
    if (!toast || held) return;
    const id = window.setTimeout(hideToast, toast.ms ?? 5000);
    return () => window.clearTimeout(id);
  }, [toast, held]);

  if (!mounted) return null;
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-[calc(72px_+_env(safe-area-inset-bottom))] z-50 flex justify-center px-4 lg:bottom-6 lg:justify-start lg:px-6"
    >
      {toast && (
        <div
          key={toast.key}
          ref={box}
          onPointerEnter={() => setHeld(true)}
          onPointerLeave={() => setHeld(!!box.current?.contains(document.activeElement))}
          onFocus={() => setHeld(true)}
          onBlur={(e) => setHeld(!!box.current?.contains(e.relatedTarget as Node | null))}
          onKeyDown={(e) => e.key === 'Escape' && hideToast()}
          className={`pointer-events-auto flex w-full max-w-[440px] items-center gap-3 rounded-xl border border-border bg-popover py-2.5 pl-4 pr-1.5 text-[13.5px] leading-snug text-popover-foreground shadow-lg transition-[transform,opacity] duration-200 motion-reduce:transition-none ${
            entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          }`}
        >
          <p className="min-w-0 flex-1 [text-wrap:pretty]">{toast.message}</p>
          {toast.link && (
            <Link
              to={toast.link.to}
              onClick={hideToast}
              className="inline-flex min-h-11 shrink-0 items-center rounded-md px-2 py-2 text-[13px] font-semibold text-primary underline-offset-4 hover:underline"
            >
              {toast.link.label}
            </Link>
          )}
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action!.run();
                hideToast();
              }}
              className="inline-flex min-h-11 shrink-0 items-center rounded-md px-2 py-2 text-[13px] font-semibold text-primary underline-offset-4 hover:underline"
            >
              {toast.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={hideToast}
            aria-label={t('Close')}
            className="grid size-11 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};
